"use client";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "convex/react";
import { formatDistanceToNow } from "date-fns";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  weaponBuildId: Id<"weaponBuilds">;
}

export function WeaponBuildComments({ weaponBuildId }: Props) {
  // Fetch comments
  const comments = useQuery(api.weaponBuilds.queries.getWeaponBuildComments, {
    weaponBuildId,
  });
  const createComment = useMutation(
    api.weaponBuilds.mutations.createWeaponBuildComment
  );
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!commentText.trim()) {
      toast.error("Comment cannot be empty", {
        description: "Comment cannot be empty",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createComment({
        weaponBuildId,
        text: commentText.trim(),
      });
      setCommentText("");
      toast.success("Comment added", {
        description: "Comment added",
      });
    } catch (error) {
      toast.error("Failed to add comment", {
        description:
          error instanceof Error ? error.message : "Failed to add comment",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  // Show loading state while fetching comments
  if (comments === undefined) {
    return (
      <div className="rounded-[8px] border border-[#262A2D] bg-[#141618] p-6">
        <div className="mb-5 flex items-baseline gap-2">
          <span className="font-mono text-[#F5A23E] text-xs tracking-[0.2em]">
            {"// COMMS"}
          </span>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-[#6a7075]" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[8px] border border-[#262A2D] bg-[#141618] p-6">
      <div className="mb-5 flex items-baseline gap-2">
        <span className="font-mono text-[#F5A23E] text-xs tracking-[0.2em]">
          {"// COMMS"}
        </span>
        <h2 className="font-bold font-heading text-[#EAEBEC] text-lg tracking-wide">
          {comments.length} COMMENTS
        </h2>
      </div>

      {/* Comment Form */}
      <form className="mb-6 space-y-3" onSubmit={handleSubmit}>
        <Textarea
          className="border-[#262A2D] bg-[#0C0D0E] text-[#EAEBEC] placeholder:text-[#6a7075]"
          maxLength={1000}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Add your intel…"
          rows={4}
          value={commentText}
        />
        <div className="flex items-center justify-between">
          <span className="font-mono text-[#6a7075] text-xs">
            {commentText.length}/1000
          </span>
          <Button
            className="rounded-[5px] bg-[#F5A23E] font-bold font-heading text-[#0a0b0c] text-xs tracking-wider hover:bg-[#ffbb63]"
            disabled={isSubmitting || !commentText.trim()}
            type="submit"
          >
            {isSubmitting ? "POSTING..." : "POST"}
          </Button>
        </div>
      </form>

      {/* Comments List */}
      <div className="flex flex-col gap-5">
        {comments.length === 0 ? (
          <p className="text-center text-[#6a7075] text-sm">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          comments.map((comment) => (
            <div className="flex gap-3" key={comment._id}>
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage
                  alt={comment.user.name}
                  src={comment.user.pictureUrl}
                />
                <AvatarFallback className="bg-[#a970d6] text-white text-xs">
                  {getInitials(comment.user.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#EAEBEC] text-sm">
                    {comment.user.name}
                  </span>
                  <span className="font-mono text-[#6a7075] text-xs">
                    {formatDistanceToNow(comment._creationTime, {
                      addSuffix: true,
                    })}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-[#B7BCC0] text-sm leading-relaxed">
                  {comment.text}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
